import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAllowedTeams, getTeamOptions, applyTeamFilter } from '../../utils/teamPermissions';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  ArrowLeft,
  Download,
  FileText,
  Calendar,
  Users,
  Trophy,
  TrendingUp,
  Filter
} from 'lucide-react';

const CoachReports = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [playerStats, setPlayerStats] = useState([]);
  const [trainingData, setTrainingData] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(() => {
    // Entrenador de Amateur empieza con "Todos", otros entrenadores con su equipo
    if (userProfile?.role === 'entrenador') {
      return userProfile.team === 'Amateur' ? 'Todos' : userProfile.team;
    }
    return 'Todos';
  });
  const [loading, setLoading] = useState(true);

  const teams = useMemo(() => getTeamOptions(userProfile), [userProfile]);

  useEffect(() => {
    if (userProfile) {
      fetchReportData();
    }
  }, [selectedTeam, userProfile]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      // Calcular allowedTeams DENTRO de la función
      const allowedTeams = getAllowedTeams(userProfile);

      // Estadísticas de jugadores
      let playersQuery = supabase
        .from('player_match_stats')
        .select('*')
        .order('total_goals', { ascending: false })
        .limit(10);

      playersQuery = applyTeamFilter(playersQuery, allowedTeams, selectedTeam);

      const { data: playersData } = await playersQuery;
      setPlayerStats(playersData || []);

      // Datos de entrenamientos (últimos 6 meses)
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      let trainingsQuery = supabase
        .from('trainings')
        .select('date, team')
        .gte('date', sixMonthsAgo.toISOString().split('T')[0])
        .order('date');

      trainingsQuery = applyTeamFilter(trainingsQuery, allowedTeams, selectedTeam);

      const { data: trainings } = await trainingsQuery;

      // Agrupar entrenamientos por mes
      const trainingsByMonth = {};
      (trainings || []).forEach(t => {
        const month = new Date(t.date).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
        trainingsByMonth[month] = (trainingsByMonth[month] || 0) + 1;
      });

      const trainingChartData = Object.entries(trainingsByMonth).map(([month, count]) => ({
        mes: month,
        entrenamientos: count
      }));

      setTrainingData(trainingChartData);

      // Datos de partidos
      let matchesQuery = supabase
        .from('matches')
        .select('result, our_score, opponent_score, team')
        .order('date', { ascending: false })
        .limit(20);

      matchesQuery = applyTeamFilter(matchesQuery, allowedTeams, selectedTeam);

      const { data: matches } = await matchesQuery;

      // Contar resultados
      const results = { victoria: 0, empate: 0, derrota: 0 };
      (matches || []).forEach(m => {
        if (m.result && results.hasOwnProperty(m.result)) {
          results[m.result]++;
        }
      });

      const matchChartData = [
        { name: 'Victorias', value: results.victoria, color: '#10b981' },
        { name: 'Empates', value: results.empate, color: '#f59e0b' },
        { name: 'Derrotas', value: results.derrota, color: '#ef4444' }
      ];

      setMatchData(matchChartData);

    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    // Título
    doc.setFontSize(20);
    doc.text('Informe del Equipo', 14, 20);

    doc.setFontSize(12);
    doc.text(`Equipo: ${selectedTeam}`, 14, 30);
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-ES')}`, 14, 37);

    // Tabla de estadísticas de jugadores
    doc.setFontSize(14);
    doc.text('Top 10 Goleadores', 14, 50);

    const tableData = playerStats.map(p => [
      `${p.first_name} ${p.last_name}`,
      p.total_matches || 0,
      p.total_goals || 0,
      p.total_assists || 0,
      p.avg_performance ? parseFloat(p.avg_performance).toFixed(1) : '-'
    ]);

    autoTable(doc, {
      startY: 55,
      head: [['Jugador', 'Partidos', 'Goles', 'Asistencias', 'Valoración']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] }
    });

    // Resultados de partidos
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text('Resultados de Partidos', 14, finalY);

    const matchTableData = matchData.map(m => [m.name, m.value]);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Resultado', 'Cantidad']],
      body: matchTableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] }
    });

    // Guardar PDF
    doc.save(`informe-${selectedTeam}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = () => {
    // Hoja 1: Estadísticas de jugadores
    const playersSheet = playerStats.map(p => ({
      'Jugador': `${p.first_name} ${p.last_name}`,
      'Equipo': p.team,
      'Partidos': p.total_matches || 0,
      'Minutos': p.total_minutes || 0,
      'Goles': p.total_goals || 0,
      'Asistencias': p.total_assists || 0,
      'Tarjetas Amarillas': p.total_yellow_cards || 0,
      'Tarjetas Rojas': p.total_red_cards || 0,
      'MVP': p.mvp_count || 0,
      'Valoración Media': p.avg_performance ? parseFloat(p.avg_performance).toFixed(1) : '-'
    }));

    // Hoja 2: Entrenamientos por mes
    const trainingsSheet = trainingData.map(t => ({
      'Mes': t.mes,
      'Entrenamientos': t.entrenamientos
    }));

    // Hoja 3: Resultados
    const resultsSheet = matchData.map(m => ({
      'Resultado': m.name,
      'Cantidad': m.value
    }));

    // Crear libro de Excel
    const wb = XLSX.utils.book_new();

    const ws1 = XLSX.utils.json_to_sheet(playersSheet);
    const ws2 = XLSX.utils.json_to_sheet(trainingsSheet);
    const ws3 = XLSX.utils.json_to_sheet(resultsSheet);

    XLSX.utils.book_append_sheet(wb, ws1, 'Jugadores');
    XLSX.utils.book_append_sheet(wb, ws2, 'Entrenamientos');
    XLSX.utils.book_append_sheet(wb, ws3, 'Resultados');

    // Guardar archivo
    XLSX.writeFile(wb, `informe-${selectedTeam}-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando informes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/coach')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Informes y Análisis</h1>
                <p className="text-sm text-gray-600">Estadísticas y reportes del equipo</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Exportar PDF
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Exportar Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        {userProfile?.role === 'admin' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-600" />
              <label className="text-sm font-medium text-gray-700">Equipo:</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Gráficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Gráfica de entrenamientos por mes */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Entrenamientos por Mes
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trainingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="entrenamientos" stroke="#8b5cf6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfica de resultados de partidos */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Resultados de Partidos
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={matchData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {matchData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top 10 Goleadores */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Top 10 Goleadores
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={playerStats.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={(p) => `${p.first_name} ${p.last_name[0]}.`} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_goals" fill="#10b981" name="Goles" />
              <Bar dataKey="total_assists" fill="#3b82f6" name="Asistencias" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla detallada */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Estadísticas Detalladas
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jugador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partidos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Minutos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Goles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asistencias
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valoración
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {playerStats.map((player, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {player.first_name} {player.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{player.team}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.total_matches || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.total_minutes || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      {player.total_goals || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                      {player.total_assists || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.avg_performance ? parseFloat(player.avg_performance).toFixed(1) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachReports;
